import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  ProjectVersionConflictError,
  useProject,
  useUpsertProject,
} from "../api";
import { UserContext } from "../auth";

import styles from "./ProjectDetailsRoute.module.css";
import {
  ALL_META_FIELDS,
  PROJECT_DETAILS_GROUPS,
  countPopulatedFields,
  emptyProjectMeta,
  metaToProjectPayload,
  projectToMeta,
  type ProjectMeta,
  type ProjectMetaField,
} from "./projectDetailsHelpers";

// Full editable Project metadata form. Lets the operator fill in
// fields the bulk import left sparse (every column of the Amazon
// tracker plus the StatusNote rows) WITHOUT having to submit an
// agent run. Ports the legacy `ProjectDetailsPanel` 1:1; the only
// behavioural change is the location (dedicated route here vs.
// embedded in the New Project view in the legacy single-file UI).
//
// Route: /projects/:projectId
export const ProjectDetailsRoute = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { data: project, isLoading, error } = useProject(projectId);
  const mutation = useUpsertProject();
  // ``UserProvider`` blocks rendering until ``user`` is resolved + the
  // org gate has passed, so ``user`` is always defined inside any
  // route. Use ``email`` as the audit-trail identifier (matches BMA
  // admin-app convention); fall back to empty string only to satisfy
  // the optional type from the context.
  const { user } = useContext(UserContext);
  const updatedBy = user?.email ?? "";

  const [meta, setMeta] = useState<ProjectMeta>(() => emptyProjectMeta());
  const [originalMeta, setOriginalMeta] = useState<ProjectMeta>(() =>
    emptyProjectMeta(),
  );
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  // Banner copy shown when a save collided with another operator's
  // edit. The 409 path inside `submit` populates this AND re-hydrates
  // the form to the server-confirmed state so the operator can see
  // what's changed before redoing their edit.
  const [conflictBanner, setConflictBanner] = useState<string | null>(null);

  // Hydrate the form once when the project loads. Re-hydrating on
  // every fetch would overwrite the operator's in-flight edits;
  // only mirror the server state at FIRST load + after a successful
  // save (handled below).
  const [hasHydrated, setHasHydrated] = useState(false);
  useEffect(() => {
    if (!project) return;
    if (hasHydrated) return;
    const hydrated = projectToMeta(project);
    setMeta(hydrated);
    setOriginalMeta(hydrated);
    setHasHydrated(true);
  }, [project, hasHydrated]);

  const populated = useMemo(() => countPopulatedFields(meta), [meta]);
  const total = ALL_META_FIELDS.length;

  // The "Saved Xs ago" pill should only show when the current form
  // state IS the saved state. Compute from a diff against
  // originalMeta (the post-save baseline) rather than clearing
  // savedAt imperatively on every keystroke - that caused the pill
  // to flicker off mid-word.
  const hasUnsavedEdits = useMemo(
    () => ALL_META_FIELDS.some((f) => meta[f] !== originalMeta[f]),
    [meta, originalMeta],
  );

  const updateField = useCallback(
    (field: ProjectMetaField, value: string) => {
      setMeta((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const submit = async () => {
    if (!projectId) return;
    setConflictBanner(null);
    const payload = metaToProjectPayload({
      meta,
      projectId,
      originalMeta,
      originalProject: project,
      updatedBy,
    });
    try {
      const saved = await mutation.mutateAsync({
        project_id: projectId,
        // Optimistic-concurrency: send the version we loaded into
        // the form. The backend writes only if the row's current
        // version still matches; otherwise it returns 409 with the
        // fresh row in the payload (handled below).
        //
        // When no project record exists yet (first save on a brand-
        // new project_id, project === null), we omit expected_version
        // — first-create is an unconditional write because there's
        // nothing to race against yet.
        ...(project ? { expected_version: project.version } : {}),
        updated_by: updatedBy,
        ...payload,
      });
      // Re-baseline so subsequent edits diff against the just-saved
      // state. Without this, a second save would re-stamp all
      // StatusNote rows on every save (the legacy regression the
      // `changed` check exists to avoid).
      const fresh = projectToMeta(saved);
      setMeta(fresh);
      setOriginalMeta(fresh);
      setSavedAt(new Date());
    } catch (e) {
      if (e instanceof ProjectVersionConflictError) {
        // Another operator edited this project between our load
        // and our save. The hook seeded the get-cache with the
        // fresh row; mirror it into the form so the operator can
        // see what's changed. Preserve their in-flight edits by
        // diffing the new server state against their local edits —
        // for now we re-hydrate fully (safer) and ask them to
        // re-apply. A merge UI is a Phase 2 add.
        const fresh = projectToMeta(e.current_project);
        setMeta(fresh);
        setOriginalMeta(fresh);
        setConflictBanner(
          `Another operator updated this project (saved as ${e.current_project.updated_by ?? "unknown user"}). ` +
            `The form has been refreshed to their version (v${e.current_project.version}); please re-apply your change and click Save again.`,
        );
        return;
      }
      // Generic error - mutation.error surfaces the message inline;
      // don't navigate, the operator should see the form still
      // populated with their unsaved edits.
      void e;
    }
  };

  if (!projectId) {
    return (
      <section className={styles.card}>
        <p className={styles.err}>
          Project ID missing from URL.{" "}
          <Link to="/operations">Back to Operations</Link>.
        </p>
      </section>
    );
  }

  if (isLoading) {
    return (
      <section className={styles.card}>
        <p className={styles.muted}>Loading project…</p>
      </section>
    );
  }

  const errMsg = mutation.error
    ? mutation.error instanceof Error
      ? mutation.error.message
      : String(mutation.error)
    : null;

  return (
    <div className={styles.page}>
      <div className={styles.headerBar}>
        <Link to="/operations" className={styles.backLink}>
          ← Operations
        </Link>
        <div className={styles.headerMeta}>
          <span>
            <code className={styles.code}>{projectId}</code>
          </span>
        </div>
      </div>

      <section className={styles.headerCard}>
        <div className={styles.headerLeft}>
          <div className={styles.eyebrow}>Project details</div>
          <h2 className={styles.title}>
            {populated} of {total} fields populated
          </h2>
          <p className={styles.lede}>
            Tracker columns from the bulk import (size, EPC, lease, status notes,
            DNO, etc.). Edit any field and click Save — these are
            project-level facts independent of any agent run. Saving here does
            not submit a new run; use{" "}
            <Link to={`/new?project=${encodeURIComponent(projectId)}`}>
              New project
            </Link>{" "}
            for that.
          </p>
        </div>
        <div className={styles.headerRight}>
          {project && (
            <span className={styles.versionLine}>
              v{project.version}
              {project.updated_by ? ` · last edit by ${project.updated_by}` : ""}
            </span>
          )}
          {savedAt && !hasUnsavedEdits && (
            <span className={styles.savedPill}>
              Saved {savedAt.toLocaleTimeString()}
            </span>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={mutation.isLoading}
            className={styles.saveBtn}
          >
            {mutation.isLoading ? "Saving…" : "Save project details"}
          </button>
        </div>
      </section>

      {conflictBanner && (
        <div className={styles.conflictBanner}>
          <strong>Edit conflict — your save was rejected.</strong>
          <p>{conflictBanner}</p>
        </div>
      )}

      {!project && !error && (
        <div className={styles.banner}>
          No existing record for <code>{projectId}</code> — saving here will
          create one. Use this when you want to record metadata for a project
          before any agent run has happened.
        </div>
      )}

      {error ? (
        <div className={styles.err}>
          Couldn't load <code>{projectId}</code>:{" "}
          <span>
            {error instanceof Error ? error.message : "Unknown error"}
          </span>
          . The form below is in the empty state - editing + saving will
          UPSERT into whatever row currently exists.
        </div>
      ) : null}

      {errMsg && (
        <div className={styles.err}>
          Save failed: {errMsg}
        </div>
      )}

      <fieldset className={styles.fieldset} disabled={mutation.isLoading}>
        {PROJECT_DETAILS_GROUPS.map((group) => (
          <section key={group.label} className={styles.groupCard}>
            <h3 className={styles.groupHead}>{group.label}</h3>
            <div className={styles.grid}>
              {group.rows.map((row) => (
                <div key={row.field} className={styles.row}>
                  <label className={styles.rowLabel} htmlFor={`f-${row.field}`}>
                    {row.label}
                  </label>
                  {row.options ? (
                    // Closed-enum field - render as <select> so the
                    // operator picks an allowed value and the
                    // backend Pydantic Literal validates. Empty
                    // option ('(none)') means "clear" - the helper
                    // emits an explicit null when this was
                    // populated originally.
                    <select
                      id={`f-${row.field}`}
                      className={styles.input}
                      value={meta[row.field] ?? ""}
                      onChange={(e) => updateField(row.field, e.target.value)}
                    >
                      <option value="">(none)</option>
                      {row.options.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  ) : row.multi ? (
                    <textarea
                      id={`f-${row.field}`}
                      className={styles.textarea}
                      value={meta[row.field] ?? ""}
                      onChange={(e) => updateField(row.field, e.target.value)}
                      placeholder={row.placeholder ?? ""}
                    />
                  ) : (
                    <input
                      id={`f-${row.field}`}
                      className={styles.input}
                      value={meta[row.field] ?? ""}
                      onChange={(e) => updateField(row.field, e.target.value)}
                      placeholder={row.placeholder ?? ""}
                      // Chrome's autofill suggests stray "John Doe"
                      // / "100 Main St" values on operator landlord
                      // / address inputs, which is noise here -
                      // operators are typing real project facts.
                      autoComplete="off"
                    />
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
      </fieldset>

      <div className={styles.footerActions}>
        <button
          type="button"
          onClick={() => navigate("/operations")}
          className={styles.cancelBtn}
          disabled={mutation.isLoading}
        >
          Back to Operations
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={mutation.isLoading}
          className={styles.saveBtn}
        >
          {mutation.isLoading ? "Saving…" : "Save project details"}
        </button>
      </div>
    </div>
  );
};
